import React, { useState, useRef, useEffect } from "react";
import { Dialog, Box, Typography, Alert, CircularProgress } from "@mui/material";

function TwoFactorModal({
  open,
  onClose,
  requireEnrollment,
  require2FA,
  qrCode,
  secret,
  onVerify,
  loading,
}) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const inputsRef = useRef([]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setDigits(["", "", "", "", "", ""]);
      setError("");
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
    }
  }, [open]);

  const code = digits.join("");

  const handleDigitChange = (idx, val) => {
    const char = val.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[idx] = char;
    setDigits(newDigits);
    if (char && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      const newDigits = [...digits];
      if (digits[idx]) {
        newDigits[idx] = "";
        setDigits(newDigits);
      } else if (idx > 0) {
        newDigits[idx - 1] = "";
        setDigits(newDigits);
        inputsRef.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    } else if (e.key === "Enter" && code.length === 6) {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(""));
      inputsRef.current[5]?.focus();
      e.preventDefault();
    }
  };

  const handleSubmit = async () => {
    if (code.length !== 6) return;
    setError("");
    try {
      await onVerify(code);
    } catch (err) {
      setError(err?.response?.data?.message || "Invalid verification code. Please try again.");
      setDigits(["", "", "", "", "", ""]);
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    }
  };

  const handleClose = () => {
    setDigits(["", "", "", "", "", ""]);
    setError("");
    onClose();
  };

  const isComplete = code.length === 6;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Blue gradient header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0d52a2, #035aa7)",
          px: 3,
          py: 2.2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {/* Shield icon */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="rgba(255,255,255,0.15)"
          />
          <polyline points="9 12 11 14 15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1rem", letterSpacing: 0.3 }}>
          {requireEnrollment ? "Set Up Two-Factor Authentication" : "Two-Factor Verification"}
        </Typography>
      </Box>

      <Box sx={{ p: "28px 28px 24px", display: "flex", flexDirection: "column", gap: 2 }}>

        {error && (
          <Alert
            severity="error"
            sx={{ borderRadius: 2, fontSize: 13 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {/* QR Code enrollment section */}
        {requireEnrollment && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" sx={{ textAlign: "center", color: "#4b5563", lineHeight: 1.7 }}>
              Scan the QR code below using your{" "}
              <strong style={{ color: "#0d52a2" }}>Google Authenticator</strong> app:
            </Typography>

            {qrCode && (
              <Box
                sx={{
                  p: 1.5,
                  border: "2px solid rgba(13,82,162,0.2)",
                  borderRadius: 3,
                  background: "#fff",
                  boxShadow: "0 4px 16px rgba(13,82,162,0.1)",
                }}
              >
                <img
                  src={qrCode}
                  alt="QR Code"
                  style={{ width: 170, height: 170, display: "block" }}
                />
              </Box>
            )}

            {secret && (
              <Box sx={{ textAlign: "center", width: "100%" }}>
                <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                  Can&apos;t scan? Enter this key manually:
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    px: 2,
                    py: 1,
                    background: "#f0f4ff",
                    border: "1px dashed rgba(13,82,162,0.3)",
                    borderRadius: 2,
                    fontFamily: "monospace",
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: 2,
                    color: "#0d52a2",
                    wordBreak: "break-all",
                  }}
                >
                  {secret}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Instruction text */}
        <Typography variant="body2" sx={{ textAlign: "center", color: "#6b7280", lineHeight: 1.7 }}>
          {requireEnrollment
            ? "After scanning, enter the 6-digit code shown in your app:"
            : "Enter the 6-digit code from your Google Authenticator app:"}
        </Typography>

        {/* 6-digit OTP input boxes */}
        <Box
          sx={{ display: "flex", gap: 1.2, justifyContent: "center" }}
          onPaste={handlePaste}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              style={{
                width: 46,
                height: 54,
                textAlign: "center",
                fontSize: "1.4rem",
                fontWeight: 700,
                color: "#0d52a2",
                background: d ? "#f0f4ff" : "rgba(255,255,255,0.9)",
                border: d ? "2px solid #0d52a2" : "2px solid #e5e7eb",
                borderRadius: 12,
                outline: "none",
                transition: "all 0.2s ease",
                boxShadow: d ? "0 0 0 4px rgba(13,82,162,0.12)" : "0 2px 6px rgba(0,0,0,0.05)",
                cursor: "text",
                fontFamily: "inherit",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#0d52a2";
                e.target.style.boxShadow = "0 0 0 4px rgba(13,82,162,0.15)";
                e.target.style.transform = "translateY(-2px)";
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "0 2px 6px rgba(0,0,0,0.05)";
                }
                e.target.style.transform = "translateY(0)";
              }}
            />
          ))}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 10,
              border: "1.5px solid #e5e7eb",
              background: "#fff",
              color: "#6b7280",
              fontWeight: 600,
              fontSize: 14,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = "#0d52a2"; e.currentTarget.style.color = "#0d52a2"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = "#6b7280"; }}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={!isComplete || loading}
            style={{
              flex: 2,
              padding: "11px",
              borderRadius: 10,
              border: "none",
              background: isComplete && !loading
                ? "linear-gradient(135deg, #0d52a2, #035aa7)"
                : "#e5e7eb",
              color: isComplete && !loading ? "#fff" : "#9ca3af",
              fontWeight: 700,
              fontSize: 14,
              cursor: isComplete && !loading ? "pointer" : "not-allowed",
              fontFamily: "inherit",
              boxShadow: isComplete && !loading ? "0 6px 20px rgba(13,82,162,0.3)" : "none",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: "#9ca3af" }} />
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                    stroke={isComplete ? "white" : "#9ca3af"} strokeWidth="2"
                    fill="rgba(255,255,255,0.15)" strokeLinecap="round" />
                  <polyline points="9 12 11 14 15 10"
                    stroke={isComplete ? "white" : "#9ca3af"} strokeWidth="2" strokeLinecap="round" />
                </svg>
                {requireEnrollment ? "Verify & Enable 2FA" : "Verify & Log In"}
              </>
            )}
          </button>
        </Box>

        {/* Helper hint */}
        <Typography variant="caption" sx={{ textAlign: "center", color: "#9ca3af" }}>
          Open Google Authenticator on your phone to get the code
        </Typography>
      </Box>
    </Dialog>
  );
}

export default TwoFactorModal;
